import React from 'react';
import { bool, func, number, object, string } from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { Form } from '../../../components';
import css from './PriceFilterForm.module.css';
import { isArrayLength } from '../../../util/genericHelpers';

const DEBOUNCE_WAIT_TIME = 400;

const CUSTOM_PRICE_OPTIONS = [
  { title: '$0-$20', min: 0, max: 20 },
  { title: '$20-$50', min: 20, max: 50 },
  { title: '$50-$75', min: 50, max: 75 },
  { title: '$75+', min: 75, max: 1000 },
];

const PriceFilterFormComponent = ({
  // 1. Define Default Values here instead of defaultProps
  liveEdit = false,
  showAsPopup = false,
  isOpen = false,
  contentRef = null,
  style = null,
  min = 0,
  step = 1,
  onCancel = null,
  onChange = null,
  onClear = null,
  onSubmit = null,
  // Props without defaults
  max,
  id,
  intl,
  ...rest
}) => {
  // Validation checks
  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  const handleChange = debounce(
    formState => {
      if (formState.dirty) {
        const { minPrice, maxPrice, ...restValues } = formState.values;
        onChange({
          minPrice: minPrice === '' ? min : minPrice,
          maxPrice: maxPrice === '' ? max : maxPrice,
          ...restValues,
        });
      }
    },
    DEBOUNCE_WAIT_TIME,
    { leading: false, trailing: true }
  );

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    return onSubmit({
      minPrice: minPrice === '' ? min : minPrice,
      maxPrice: maxPrice === '' ? max : maxPrice,
      ...restValues,
    });
  };

  const formCallbacks = liveEdit
    ? { onSubmit: () => null }
    : { onSubmit: handleSubmit, onCancel, onClear };

  return (
    <FinalForm
      {...rest}
      {...formCallbacks}
      render={formRenderProps => {
        const { form, handleSubmit, values, children } = formRenderProps;

        const handleCancel = () => {
          form.reset();
          if (onCancel) onCancel();
        };

        const activePillIndex = CUSTOM_PRICE_OPTIONS.findIndex(
          item => item.min === values.minPrice && item.max === values.maxPrice
        );

        const handlePillToggle = item => {
          const isCurrentlyActive = item.min === values.minPrice && item.max === values.maxPrice;

          if (isCurrentlyActive) {
            form.reset();
            if (onClear) onClear();
          } else {
            form.batch(() => {
              form.change('minPrice', item.min);
              form.change('maxPrice', item.max);
            });

            if (liveEdit) {
              onChange({ ...values, minPrice: item.min, maxPrice: item.max });
            }
          }
        };

        const classes = classNames(css.root, {
          [css.popup]: showAsPopup,
          [css.isOpenAsPopup]: showAsPopup && isOpen,
          [css.plain]: !showAsPopup,
          [css.isOpen]: !showAsPopup && isOpen,
        });

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            tabIndex="0"
            contentRef={contentRef}
            style={{ minWidth: '300px', ...style }}
          >
            <div className={css.contentWrapper}>
              {showAsPopup && (
                <span className={css.label}>
                  <FormattedMessage id="PriceFilterForm.label" />
                </span>
              )}
            </div>

            <div className={css.pricePillsWrapper}>
              {isArrayLength(CUSTOM_PRICE_OPTIONS) &&
                CUSTOM_PRICE_OPTIONS.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    className={classNames(css.pricePill, {
                      [css.active]: activePillIndex === i,
                    })}
                    onClick={() => handlePillToggle(item)}
                  >
                    {item.title}
                  </button>
                ))}
            </div>

            {liveEdit ? (
              <FormSpy onChange={handleChange} subscription={{ values: true, dirty: true }} />
            ) : (
              <div className={css.buttonsWrapper}>
                <button className={css.clearButton} type="button" onClick={onClear}>
                  <FormattedMessage id="PriceFilterForm.clear" />
                </button>
                <button className={css.cancelButton} type="button" onClick={handleCancel}>
                  <FormattedMessage id="PriceFilterForm.cancel" />
                </button>
                <button className={css.submitButton} type="submit">
                  <FormattedMessage id="PriceFilterForm.submit" />
                </button>
              </div>
            )}
            {children}
          </Form>
        );
      }}
    />
  );
};

PriceFilterFormComponent.propTypes = {
  id: string.isRequired,
  liveEdit: bool,
  showAsPopup: bool,
  onCancel: func,
  onChange: func,
  onClear: func,
  onSubmit: func,
  isOpen: bool,
  contentRef: func,
  style: object,
  min: number.isRequired,
  max: number.isRequired,
  step: number,
  intl: intlShape.isRequired,
};

const PriceFilterForm = injectIntl(PriceFilterFormComponent);

export default PriceFilterForm;
